/**
 * Content and sample data for the public marketing pages: the landing page
 * and /get-started. Copy is verbatim from the approved design's content
 * inventory (2 Oct 2026); keep its typographic characters (’ “ ” – — − → ↳ ↗
 * ≤ ≈) when editing. What is for sale is not here: plans, prices and the
 * billing answers are in pricing.ts, read from src/shared/billing/.
 *
 * SAMPLE DATA. The deals below are illustrative, on a clock that starts at
 * 25 Sep 2026 10:00 BST (T0). Lindis Blood Care, Tessel Labs, Cognivix and
 * Miso Robotics follow the design; AKT London, The Football Factory,
 * Foundermatcha, Elona Health, LSG Inc. and DIT AgTech are
 * brief §5.2's sample rows; the rest are invented to give every region and
 * the rule builder something to show. Ledgerline and Halden Pay launched in
 * the week before T0 so the sample Weekly Sonar has new raises to list, and
 * the two perks are invented for the Perk expiring email. On the landing
 * page they appear only in the sample alerts, each labelled "Sample alert —
 * illustrative"; everything it calls live is a real raise (live.ts).
 * /get-started's preview still replays them.
 *
 * Platform regimes and minimums are from the platform directory
 * (migration 202609271400_platform_directory.sql, checked 25–27 Sep 2026);
 * where it states no floor, the note it does state is used instead.
 */

export type Region = 'US' | 'UK' | 'EU' | 'AU/NZ' | 'CA' | 'Asia' | 'LatAm';

export const REGIONS: Region[] = [
  'US',
  'UK',
  'EU',
  'AU/NZ',
  'CA',
  'Asia',
  'LatAm',
];

/** Sample-data clock: 25 Sep 2026 09:00 UTC, which is 10:00 BST. */
export const T0 = Date.UTC(2026, 8, 25, 9, 0, 0);

const at = (iso: string) => Date.parse(iso);
const minsBefore = (n: number) => T0 - n * 60_000;

/** Units of each currency per US dollar ($1 = £0.746). Sample rates. */
export const FX: Record<string, number> = {
  USD: 1,
  GBP: 0.746,
  EUR: 0.855,
  AUD: 1.52,
  CAD: 1.37,
  NZD: 1.68,
  MYR: 4.21,
  BRL: 5.36,
};

// ---------------------------------------------------------------------------
// Platforms
// ---------------------------------------------------------------------------

export interface SitePlatform {
  id: string;
  name: string;
  /** Placeholder mark until logos are licensed. */
  mono: string;
  region: Region;
  regime: string;
  /** "$100", "set per raise", or null where the platform states nothing. */
  min: string | null;
  planned?: boolean;
}

/** SEC EDGAR is a source, not a platform: it leads the US row and step 01. */
export const EDGAR = {
  id: 'edgar',
  name: 'SEC EDGAR · Form C',
  mono: 'SEC',
  regime: 'Reg CF',
  min: null,
} as const;

export const PLATFORMS: SitePlatform[] = [
  {
    id: 'wefunder',
    name: 'Wefunder',
    mono: 'W',
    region: 'US',
    regime: 'Reg CF',
    min: '$100',
  },
  {
    id: 'startengine',
    name: 'StartEngine',
    mono: 'SE',
    region: 'US',
    regime: 'Reg CF',
    min: 'set by issuer',
  },
  {
    id: 'republic',
    name: 'Republic',
    mono: 'R',
    region: 'US',
    regime: 'Reg CF',
    min: '$150',
  },
  {
    id: 'dealmaker',
    name: 'DealMaker (hosted)',
    mono: 'DM',
    region: 'US',
    regime: 'Reg A+',
    min: 'set by issuer',
  },
  {
    id: 'netcapital',
    name: 'Netcapital',
    mono: 'NC',
    region: 'US',
    regime: 'Reg CF',
    min: '$99',
  },
  {
    id: 'crowdcube',
    name: 'Crowdcube',
    mono: 'CC',
    region: 'UK',
    regime: 'FCA',
    min: '£10',
  },
  {
    id: 'republic-europe',
    name: 'Republic Europe',
    mono: 'RE',
    region: 'UK',
    regime: 'FCA',
    min: '£10',
  },
  {
    id: 'companisto',
    name: 'Companisto',
    mono: 'Co',
    region: 'EU',
    regime: 'ECSPR',
    min: '€250',
  },
  {
    id: 'fundernation',
    name: 'FunderNation',
    mono: 'FN',
    region: 'EU',
    regime: '§34f',
    min: '€100',
  },
  {
    id: 'invesdor',
    name: 'Invesdor',
    mono: 'In',
    region: 'EU',
    regime: 'ECSPR',
    min: '€250',
  },
  {
    id: 'seedblink',
    name: 'SeedBlink',
    mono: 'SB',
    region: 'EU',
    regime: 'ECSPR',
    min: 'set per raise',
  },
  {
    id: 'conda',
    name: 'CONDA',
    mono: 'CD',
    region: 'EU',
    regime: 'ECSPR',
    min: '€100',
  },
  {
    id: 'capital-cell',
    name: 'Capital Cell',
    mono: 'CCl',
    region: 'EU',
    regime: 'ECSPR',
    min: '€500',
  },
  {
    id: 'mamacrowd',
    name: 'Mamacrowd',
    mono: 'Ma',
    region: 'EU',
    regime: 'ECSPR',
    min: '€250',
  },
  {
    id: 'crowdfundme',
    name: 'CrowdFundMe',
    mono: 'CFM',
    region: 'EU',
    regime: 'ECSPR',
    min: 'usually €250',
  },
  {
    id: 'opstart',
    name: 'Opstart',
    mono: 'Op',
    region: 'EU',
    regime: 'ECSPR',
    min: null,
    planned: true,
  },
  {
    id: 'spreds',
    name: 'Spreds',
    mono: 'Sp',
    region: 'EU',
    regime: 'ECSPR',
    min: null,
  },
  {
    id: 'crowdberry',
    name: 'Crowdberry',
    mono: 'CB',
    region: 'EU',
    regime: 'ECSPR',
    min: '€1,000',
    planned: true,
  },
  {
    id: 'tudigo',
    name: 'Tudigo',
    mono: 'Tu',
    region: 'EU',
    regime: 'ECSPR',
    min: null,
  },
  {
    id: 'birchal',
    name: 'Birchal',
    mono: 'Bi',
    region: 'AU/NZ',
    regime: 'CSF',
    min: null,
  },
  {
    id: 'onmarket',
    name: 'OnMarket',
    mono: 'OM',
    region: 'AU/NZ',
    regime: 'CSF',
    min: 'usually A$250–500',
  },
  {
    id: 'pledgeme',
    name: 'PledgeMe',
    mono: 'PM',
    region: 'AU/NZ',
    regime: 'FMC Act',
    min: 'set per raise',
  },
  {
    id: 'frontfundr',
    name: 'FrontFundr',
    mono: 'FF',
    region: 'CA',
    regime: 'NI 45-110',
    min: 'C$100',
  },
  {
    id: 'pitchin',
    name: 'pitchIN',
    mono: 'pI',
    region: 'Asia',
    regime: 'SC-RMO',
    min: null,
  },
  {
    id: 'kria',
    name: 'Kria',
    mono: 'Kr',
    region: 'LatAm',
    regime: 'CVM',
    min: 'from R$1,000',
    planned: true,
  },
  {
    id: 'captable',
    name: 'CapTable',
    mono: 'CT',
    region: 'LatAm',
    regime: 'CVM',
    min: 'usually R$1,000–3,000',
    planned: true,
  },
];

export function platformById(id: string): { name: string; mono: string } {
  if (id === EDGAR.id) return EDGAR;
  const p = PLATFORMS.find((x) => x.id === id);
  if (!p) throw new Error(`Unknown platform ${id}`);
  return p;
}

// ---------------------------------------------------------------------------
// Sample deals
// ---------------------------------------------------------------------------

export type Eligibility = 'foreign' | 'residents' | 'accredited';

export interface SampleDeal {
  id: string;
  name: string;
  platform: string;
  regime: string;
  region: Region;
  /** ISO code of the market the raise is offered in, for "Residents only". */
  country: string;
  sector: string;
  currency: string;
  raised: number | null;
  target: number | null;
  max: number | null;
  investors: number | null;
  minTicket: number;
  terms: string;
  launchAt: number;
  closesAt: number;
  checkedAt: number;
  openTo: Eligibility;
  extended?: boolean;
  /** Provenance for a raise known only from a filing. */
  filed?: { form: string; at: number };
  /** An early-bird bonus or discount, and when it ends. */
  perk?: { text: string; endsAt: number };
}

export const DEALS: SampleDeal[] = [
  {
    id: 'lindis',
    name: 'Lindis Blood Care',
    platform: 'companisto',
    regime: 'ECSPR',
    region: 'EU',
    country: 'DE',
    sector: 'Medtech',
    currency: 'EUR',
    raised: 2_498_000,
    target: 500_000,
    max: 2_500_000,
    investors: 1_212,
    minTicket: 250,
    terms: 'Profit-participating loan',
    launchAt: at('2026-08-20T08:00:00Z'),
    closesAt: at('2026-09-27T16:00:00Z'),
    checkedAt: minsBefore(4),
    openTo: 'foreign',
  },
  {
    id: 'tessel',
    name: 'Tessel Labs',
    platform: 'wefunder',
    regime: 'Reg CF',
    region: 'US',
    country: 'US',
    sector: 'SaaS',
    currency: 'USD',
    raised: 904_300,
    target: 125_000,
    max: 1_235_000,
    investors: 1_018,
    minTicket: 100,
    terms: 'SAFE · $18M cap',
    launchAt: at('2026-08-04T14:00:00Z'),
    closesAt: at('2026-09-28T03:59:00Z'),
    checkedAt: minsBefore(6),
    openTo: 'foreign',
    perk: {
      text: 'Early-bird bonus of 10% extra shares',
      endsAt: at('2026-09-27T03:59:00Z'),
    },
  },
  {
    id: 'cognivix',
    name: 'Cognivix',
    platform: 'republic',
    regime: 'Reg CF',
    region: 'US',
    country: 'US',
    sector: 'Healthtech',
    currency: 'USD',
    raised: 371_854,
    target: 50_000,
    max: 1_235_000,
    investors: 140,
    minTicket: 150,
    terms: 'Common stock',
    launchAt: at('2026-08-11T14:00:00Z'),
    closesAt: at('2026-10-02T03:59:00Z'),
    checkedAt: minsBefore(12),
    openTo: 'foreign',
  },
  {
    id: 'miso',
    name: 'Miso Robotics',
    platform: 'republic',
    regime: 'Reg A+',
    region: 'US',
    country: 'US',
    sector: 'Robotics',
    currency: 'USD',
    raised: 56_165,
    target: null,
    max: null,
    investors: 29,
    minTicket: 500,
    terms: 'Common stock',
    launchAt: at('2026-09-24T15:20:00Z'),
    closesAt: at('2026-12-18T04:59:00Z'),
    checkedAt: minsBefore(12),
    openTo: 'foreign',
  },
  {
    id: 'akt',
    name: 'AKT London',
    platform: 'republic-europe',
    regime: 'FCA',
    region: 'UK',
    country: 'GB',
    sector: 'Sports & leisure',
    currency: 'GBP',
    raised: 327_000,
    target: 300_000,
    max: 600_000,
    investors: 412,
    minTicket: 10,
    terms: 'Ordinary shares',
    launchAt: at('2026-09-01T08:00:00Z'),
    closesAt: at('2026-09-30T22:59:00Z'),
    checkedAt: minsBefore(9),
    openTo: 'foreign',
  },
  {
    id: 'football-factory',
    name: 'The Football Factory',
    platform: 'republic-europe',
    regime: 'FCA',
    region: 'UK',
    country: 'GB',
    sector: 'Sports & leisure',
    currency: 'GBP',
    raised: 199_500,
    target: 150_000,
    max: 400_000,
    investors: 288,
    minTicket: 10,
    terms: 'Ordinary shares',
    launchAt: at('2026-09-03T08:00:00Z'),
    closesAt: at('2026-09-30T21:00:00Z'),
    checkedAt: minsBefore(9),
    openTo: 'foreign',
  },
  {
    id: 'foundermatcha',
    name: 'Foundermatcha',
    platform: 'republic-europe',
    regime: 'FCA',
    region: 'UK',
    country: 'GB',
    sector: 'Food & drink',
    currency: 'GBP',
    raised: 235_000,
    target: 250_000,
    max: 500_000,
    investors: 361,
    minTicket: 10,
    terms: 'Ordinary shares',
    launchAt: at('2026-09-15T08:00:00Z'),
    closesAt: at('2026-10-16T22:59:00Z'),
    checkedAt: minsBefore(9),
    openTo: 'foreign',
  },
  {
    id: 'elona',
    name: 'Elona Health',
    platform: 'companisto',
    regime: 'ECSPR',
    region: 'EU',
    country: 'DE',
    sector: 'Healthtech',
    currency: 'EUR',
    raised: 540_750,
    target: 100_000,
    max: 550_000,
    investors: 604,
    minTicket: 250,
    terms: 'Profit-participating loan',
    launchAt: at('2026-09-08T08:00:00Z'),
    closesAt: at('2026-10-09T21:59:00Z'),
    checkedAt: minsBefore(4),
    openTo: 'foreign',
  },
  {
    id: 'dit-agtech',
    name: 'DIT AgTech',
    platform: 'edgar',
    regime: 'Reg CF',
    region: 'US',
    country: 'US',
    sector: 'AgTech',
    currency: 'USD',
    raised: null,
    target: 25_000,
    max: 1_235_000,
    investors: null,
    minTicket: 250,
    terms: 'Common stock',
    launchAt: at('2026-09-10T14:00:00Z'),
    closesAt: at('2026-11-30T04:59:00Z'),
    checkedAt: minsBefore(20),
    openTo: 'foreign',
    filed: { form: 'SEC Form C/A', at: at('2026-09-25T00:00:00Z') },
  },
  {
    id: 'ledgerline',
    name: 'Ledgerline',
    platform: 'crowdcube',
    regime: 'FCA',
    region: 'UK',
    country: 'GB',
    sector: 'Fintech',
    currency: 'GBP',
    raised: 612_400,
    target: 400_000,
    max: 900_000,
    investors: 977,
    minTicket: 10,
    terms: 'Ordinary shares · £18M pre-money',
    launchAt: at('2026-09-21T08:00:00Z'),
    closesAt: at('2026-10-14T22:59:00Z'),
    checkedAt: minsBefore(3),
    openTo: 'foreign',
    perk: {
      text: 'Early-bird discount of 5%',
      endsAt: at('2026-10-01T22:59:00Z'),
    },
  },
  {
    id: 'fennic',
    name: 'Fennic Data',
    platform: 'invesdor',
    regime: 'ECSPR',
    region: 'EU',
    country: 'FI',
    sector: 'SaaS',
    currency: 'EUR',
    raised: 318_000,
    target: 250_000,
    max: 1_000_000,
    investors: 233,
    minTicket: 250,
    terms: 'Ordinary shares · €9M pre-money',
    launchAt: at('2026-08-25T07:00:00Z'),
    closesAt: at('2026-10-20T20:59:00Z'),
    checkedAt: minsBefore(11),
    openTo: 'foreign',
    extended: true,
  },
  {
    id: 'halden',
    name: 'Halden Pay',
    platform: 'mamacrowd',
    regime: 'ECSPR',
    region: 'EU',
    country: 'IT',
    sector: 'Fintech',
    currency: 'EUR',
    raised: 156_000,
    target: 150_000,
    max: 600_000,
    investors: 181,
    minTicket: 250,
    terms: 'Ordinary shares · €6.5M pre-money',
    launchAt: at('2026-09-22T07:00:00Z'),
    closesAt: at('2026-10-12T21:59:00Z'),
    checkedAt: minsBefore(7),
    openTo: 'foreign',
  },
  {
    id: 'brightgrid',
    name: 'Brightgrid Energy',
    platform: 'birchal',
    regime: 'CSF',
    region: 'AU/NZ',
    country: 'AU',
    sector: 'Energy',
    currency: 'AUD',
    raised: 842_000,
    target: 250_000,
    max: 3_000_000,
    investors: 1_305,
    minTicket: 50,
    terms: 'Ordinary shares · A$14M pre-money',
    launchAt: at('2026-09-09T22:00:00Z'),
    closesAt: at('2026-10-06T12:59:00Z'),
    checkedAt: minsBefore(8),
    openTo: 'residents',
  },
  {
    id: 'prairie-oat',
    name: 'Prairie Oat Co.',
    platform: 'frontfundr',
    regime: 'NI 45-110',
    region: 'CA',
    country: 'CA',
    sector: 'Food & drink',
    currency: 'CAD',
    raised: 212_000,
    target: 150_000,
    max: 500_000,
    investors: 402,
    minTicket: 100,
    terms: 'Common shares · C$4M pre-money',
    launchAt: at('2026-09-12T15:00:00Z'),
    closesAt: at('2026-10-18T06:59:00Z'),
    checkedAt: minsBefore(14),
    openTo: 'residents',
  },
  {
    id: 'kopi-kita',
    name: 'Kopi Kita',
    platform: 'pitchin',
    regime: 'SC-RMO',
    region: 'Asia',
    country: 'MY',
    sector: 'Food & drink',
    currency: 'MYR',
    raised: 1_450_000,
    target: 1_000_000,
    max: 3_000_000,
    investors: 538,
    minTicket: 100,
    terms: 'Ordinary shares · RM24M pre-money',
    launchAt: at('2026-09-06T01:00:00Z'),
    closesAt: at('2026-10-26T15:59:00Z'),
    checkedAt: minsBefore(13),
    openTo: 'foreign',
  },
  {
    id: 'lsg',
    name: 'LSG Inc.',
    platform: 'startengine',
    regime: 'Reg CF',
    region: 'US',
    country: 'US',
    sector: 'Consumer',
    currency: 'USD',
    raised: null,
    target: 15_000,
    max: 1_234_995,
    investors: null,
    minTicket: 300,
    terms: 'Common stock at $7.50',
    launchAt: at('2026-10-01T13:00:00Z'),
    closesAt: at('2026-12-24T04:59:00Z'),
    checkedAt: minsBefore(10),
    openTo: 'foreign',
    filed: { form: 'SEC Form C', at: at('2026-09-24T00:00:00Z') },
  },
];

export function dealById(id: string): SampleDeal {
  const d = DEALS.find((x) => x.id === id);
  if (!d) throw new Error(`Unknown deal ${id}`);
  return d;
}

// ---------------------------------------------------------------------------
// Landing: hero stories
// ---------------------------------------------------------------------------

export interface Story {
  id: string;
  kicker: string;
  value: number;
  headline: string;
}

export const STORIES: Story[] = [
  {
    id: 'revolut',
    kicker: 'Revolut · 2016 → 2024',
    value: 40_000,
    headline:
      'Revolut’s first crowdfunding investors paid $2.14 a share in 2016. Eight years later they were offered $865.42 — a 40,000% gain — and 433 people had got in for as little as £10.',
  },
  {
    id: 'monzo',
    kicker: 'Monzo · 96 seconds in 2016',
    value: 2_700,
    headline:
      '1,800 people put £1m into Monzo in 96 seconds. The ones who held turned every £1,000 into roughly £28,000 — a 2,700% return — without ever being a VC.',
  },
];

// ---------------------------------------------------------------------------
// Landing: crowd rounds that paid out
// ---------------------------------------------------------------------------

export type ExitRegion = 'UK' | 'Europe' | 'US';

export interface CrowdExit {
  id: string;
  name: string;
  meta: string;
  region: ExitRegion;
  currency: 'GBP' | 'EUR' | 'USD';
  /** Return in percent; null where it was never disclosed. */
  ret: number | null;
  stat: string;
  kind: 'realised' | 'paper';
  detail: string;
}

export const EXITS: CrowdExit[] = [
  {
    id: 'revolut',
    name: 'Revolut',
    meta: 'Crowdcube · 2016 → 2024 share sale',
    region: 'UK',
    currency: 'GBP',
    ret: 40_000,
    stat: '+40,000%',
    kind: 'realised',
    detail:
      '£1.01m on Crowdcube at a £42m valuation (2016). Investors were up 1,800% by 2018 and ~40,000% by the 2024 share sale, when Crowdcube and Seedrs holders were allocated $19m to cash out.',
  },
  {
    id: 'zigzag',
    name: 'ZigZag Global',
    meta: '2015 → acquired by Global Blue',
    region: 'UK',
    currency: 'GBP',
    ret: 3_700,
    stat: '+3,700%',
    kind: 'realised',
    detail: '£100k from the crowd in 2015, bought by Global Blue. +3,700%.',
  },
  {
    id: 'contractpodai',
    name: 'ContractPodAi',
    meta: 'Insight Partners bought in',
    region: 'UK',
    currency: 'GBP',
    ret: 2_900,
    stat: '+2,900%',
    kind: 'realised',
    detail: 'Crowdfunded, then Insight Partners bought in. +2,900%.',
  },
  {
    id: 'monzo',
    name: 'Monzo',
    meta: 'Crowdcube · 2016 → latest mark',
    region: 'UK',
    currency: 'GBP',
    ret: 2_700,
    stat: '+2,700%',
    kind: 'paper',
    detail:
      'Three Crowdcube rounds. First at 51p a share, last at £7.72. Latest mark: £14.41. The 2016 crowd is up ~2,700%.',
  },
  {
    id: 'officernd',
    name: 'OfficeRnD',
    meta: 'Seedrs · incl. SEIS/EIS relief',
    region: 'UK',
    currency: 'GBP',
    ret: 2_360,
    stat: '+2,360%',
    kind: 'realised',
    detail:
      'Seedrs investors made 7.8x, or 24.6x once SEIS/EIS relief is counted. That’s a 2,360% return on a coworking software company most people never heard of.',
  },
  {
    id: 'beta-bionics',
    name: 'Beta Bionics',
    meta: 'Wefunder · 2016 → 2025 Nasdaq IPO',
    region: 'US',
    currency: 'USD',
    ret: 900,
    stat: '10x',
    kind: 'paper',
    detail:
      '$1m from 718 Wefunder investors at a $100m valuation in 2016. Nasdaq IPO in 2025 at ~$1bn. 10x on paper.',
  },
  {
    id: 'mettrr',
    name: 'Mettrr',
    meta: '2012 → 2017 secondary sale',
    region: 'UK',
    currency: 'GBP',
    ret: 800,
    stat: '+800%',
    kind: 'realised',
    detail:
      '25 people invested £100k in 2012. In 2017 they sold for 9x — +800% — in the first secondary sale UK crowdfunding had ever seen.',
  },
  {
    id: 'koro',
    name: 'KoRo',
    meta: 'Germany · Companisto → HV Capital, 2022',
    region: 'Europe',
    currency: 'EUR',
    ret: 500,
    stat: '+500%',
    kind: 'realised',
    detail:
      '€1m from 1,100 small investors on Companisto. Sold to HV Capital in 2022: +500%, the platform’s biggest exit ever.',
  },
  {
    id: 'cityvarasto',
    name: 'Cityvarasto',
    meta: 'Finland · Invesdor · 2015 → 2025 IPO',
    region: 'Europe',
    currency: 'EUR',
    ret: 425,
    stat: '+425%',
    kind: 'realised',
    detail:
      '€1m raised on Invesdor in 2015 at a €24m valuation. IPO’d on Nasdaq Helsinki in October 2025 at €128m: +425%.',
  },
  {
    id: 'e-car-club',
    name: 'E-Car Club',
    meta: '2013 → Europcar, 2015',
    region: 'UK',
    currency: 'GBP',
    ret: 200,
    stat: '+200%',
    kind: 'realised',
    detail:
      '£100k from 63 people in 2013, sold to Europcar in 2015. +200%, and the first crowdfunding exit anywhere in the world.',
  },
  {
    id: 'camden-town',
    name: 'Camden Town Brewery',
    meta: '2015 → AB InBev, eight months later',
    region: 'UK',
    currency: 'GBP',
    ret: 100,
    stat: '+100%',
    kind: 'realised',
    detail:
      '2,173 people bought in at 64p a share in April 2015. AB InBev bought the brewery eight months later at up to £1.25. Up to +100% in eight months.',
  },
  {
    id: 'beatbox',
    name: 'BeatBox Beverages',
    meta: 'Wefunder · 2022 → Anheuser-Busch, 2026',
    region: 'US',
    currency: 'USD',
    ret: null,
    stat: '$490m',
    kind: 'realised',
    detail:
      'A Wefunder campaign in 2022. Anheuser-Busch paid $490m for a majority stake in 2026. The largest crowdfunding exit in US history.',
  },
];

// ---------------------------------------------------------------------------
// Landing: the part the platforms don't put on the homepage
// ---------------------------------------------------------------------------

export const OUTCOMES = [
  {
    id: 'exited',
    label: 'Exited',
    count: 6,
    tone: 'gain',
    meaning:
      'Acquisition, IPO or secondary sale. The only point most backers get cash back, usually years after investing.',
  },
  {
    id: 'bust',
    label: 'Went bust',
    count: 20,
    tone: 'loss',
    meaning:
      'Shares are typically written off. UK investors who claimed EIS or SEIS relief may be able to offset the loss against tax.',
  },
  {
    id: 'neither',
    label: 'Neither, yet',
    count: 74,
    tone: 'none',
    meaning:
      'Still trading, or quietly dormant. There’s no market to sell into unless the platform opens a secondary window, so your money stays locked in.',
  },
] as const;

export const FREETRADE = [
  {
    id: 'early',
    label: 'Earliest backers',
    stat: '15x',
    statLabel: 'Earliest backers',
    per1000: 15_000,
    tone: 'gain',
  },
  {
    id: 'peak',
    label: '2021 peak',
    stat: '−87%',
    statLabel: 'Bought at the 2021 peak',
    per1000: 130,
    tone: 'loss',
  },
] as const;

// ---------------------------------------------------------------------------
// Landing: how it works
// ---------------------------------------------------------------------------

/**
 * `platforms` is the site's count of the platforms it reads ("26",
 * site/facts.ts), null where it is unknown; `edgar`, whether SEC EDGAR is
 * read.
 */
export const steps = (platforms: string | null, edgar: boolean) => [
  {
    n: '01',
    title: `We watch ${platforms ?? 'the'} platforms${edgar ? ' and SEC filings' : ''}`,
    body: edgar
      ? 'Every public campaign page across the US, UK, EU, Australia, New Zealand, Canada, Asia and Latin America, plus every Form C on EDGAR, including raises with no public marketplace.'
      : 'Every public campaign page across the US, UK, EU, Australia, New Zealand, Canada, Asia and Latin America.',
  },
  {
    n: '02',
    title: 'You choose regions, sectors and lead times',
    body: 'Set rules in plain sentences: “EU + UK · SaaS, Fintech · min ticket ≤ €500”. Tell us where you live and we badge what you can invest in.',
  },
  {
    n: '03',
    title: 'You’re alerted when it matters',
    body: 'At launch, 7 days, 72 hours and 24 hours before close, at 90% of cap and before an early-bird perk expires. By email.',
  },
];

export const RULE_REGIONS: Region[] = ['EU', 'UK', 'US'];
export const RULE_SECTORS = ['SaaS', 'Fintech', 'Healthtech', 'Food & drink'];
export const RULE_MAX_TICKET_EUR = 500;

/** Where each marker sits on the alert timeline, as a fraction of the track. */
export const TIMELINE = [
  { id: 'launch', label: 'Launch', at: 0, kicker: 'NEW RAISE', tone: 'accent' },
  {
    id: 'perk',
    label: 'Perk expires',
    at: 0.2,
    kicker: 'EARLY-BIRD PERK ENDING',
    tone: 'urgent',
  },
  { id: '7d', label: '7d', at: 0.42, kicker: '7 DAYS LEFT', tone: 'neutral' },
  {
    id: 'cap',
    label: '90% of cap',
    at: 0.58,
    kicker: 'NEAR CAP · 90%',
    tone: 'cap',
  },
  {
    id: '72h',
    label: '72h',
    at: 0.74,
    kicker: '72 HOURS LEFT',
    tone: 'neutral',
  },
  {
    id: '24h',
    label: '24h',
    at: 0.88,
    kicker: '24 HOURS LEFT',
    tone: 'neutral',
  },
  { id: 'close', label: 'Close', at: 1, kicker: null, tone: 'neutral' },
] as const;

// ---------------------------------------------------------------------------
// Questions
// ---------------------------------------------------------------------------

export interface Faq {
  q: string;
  a: string;
}

/**
 * The landing page's questions. The last one it asks, how to change or cancel
 * a plan, is /pricing's answer in /pricing's words (CANCEL_FAQ in pricing.ts).
 */
export const FAQS: Faq[] = [
  {
    q: 'Can I invest in every raise I see?',
    a: 'No. Each platform decides who can invest, usually by country of residence. Tell us where you live and every raise is badged “Open to foreign investors”, “Residents only” or “Accredited only”. Raises you can’t join are greyed out, never hidden, so you can still follow them.',
  },
  {
    q: 'Is this investment advice?',
    a: 'No. Raise Sonar is an information service. We don’t recommend deals, take money or process investments. Every button opens the platform, where you decide and invest.',
  },
  {
    q: 'How fast do alerts arrive?',
    a: 'Launch alerts go out within minutes of a raise appearing on a platform or a Form C appearing on EDGAR. Most platforms are checked every 5–15 minutes, and every figure shows when it was last checked. Free subscribers get the weekly digest, 24–48 hours behind.',
  },
];

// ---------------------------------------------------------------------------
// Get started
// ---------------------------------------------------------------------------

export const SECTORS = [
  'SaaS',
  'Fintech',
  'Healthtech',
  'Medtech',
  'Robotics',
  'AgTech',
  'Energy',
  'Food & drink',
  'Consumer',
  'Sports & leisure',
];

/**
 * The rule builder's four steps: the tab's name, the heading and the line
 * under it. `trialDays` is TRIAL_DAYS (readTrialDays), not the design's
 * "7 days".
 */
export const onboardingSteps = (trialDays: number) => [
  {
    name: 'Where',
    title: 'Where should we look?',
    text: 'Pick the markets to watch. Tell us where you live and we’ll mark the raises you can invest in.',
  },
  {
    name: 'What',
    title: 'What kind of companies?',
    text: 'Leave sectors empty to hear about every sector.',
  },
  {
    name: 'When',
    title: 'When should we tell you?',
    text: 'Most people pick launch plus two closing reminders. Quiet hours hold alerts overnight in your timezone.',
  },
  {
    name: 'Sign up',
    title: 'Create a free account to set up rules like this',
    text: `Rules are part of the Alerts plan. Create a free account to set up this rule and as many others as you like, with your first ${trialDays} days of Alerts free.`,
  },
];
